import React, { useState } from 'react';
import {
    X,
    MapPin,
    Phone,
    Truck,
    CreditCard,
    Calendar,
    Package,
    ChevronDown,
    AlertCircle,
    CheckCircle2,
    StickyNote,
    Camera,
    History,
    MessageSquare,
    Navigation,
    User
} from 'lucide-react';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { computeCollectionAmounts, isCreditOrder } from '../utils/payments';
import OrderTimeline from './OrderTimeline';

const OrderDetailsModal = ({ isOpen, order, onClose }) => {
    const [showItems, setShowItems] = useState(true);
    const [showTimeline, setShowTimeline] = useState(false);

    if (!isOpen || !order) return null;

    const amountsBase = computeCollectionAmounts(order);
    const paid = Number(order?.paid_amount || order?.amount_paid || 0);
    const totalAmt = Number(order?.totalAmount || order?.total_amount || order?.paymentAmount || order?.payment_amount || 0);
    const internalOrdersTotal = Number(order?.internal_orders_total || 0);
    const shippingCost = Number(order?.shipping_cost || 0);
    const fullTotal = totalAmt + internalOrdersTotal + shippingCost;

    const safeAmounts = (() => {
        if (isCreditOrder(order)) return { productDue: 0, shippingDue: 0, totalDue: 0 };
        if (order?.delivered_at || ['entregado_cliente', 'entregado_bodega', 'entregado', 'finalizado'].includes(order?.status?.toLowerCase())) {
            return { productDue: 0, shippingDue: 0, totalDue: 0 };
        }
        if (amountsBase.totalDue > 0) return amountsBase;
        const simpleDue = Math.max(fullTotal - paid, 0);
        return { productDue: simpleDue, shippingDue: 0, totalDue: simpleDue };
    })();

    const { totalDue } = safeAmounts;
    const isCredit = isCreditOrder(order);

    const getPaymentMethodLabel = (method) => {
        const map = {
            efectivo: 'Efectivo',
            transferencia: 'Transferencia',
            cliente_credito: 'Crédito',
            pago_electronico: 'Pago Electrónico',
            contraentrega: 'Contraentrega',
            publicidad: 'Publicidad',
            reposicion: 'Reposición'
        };
        const key = (method || '').toLowerCase();
        return map[key] || method || 'No especificado';
    };

    const getDeliveryMethodLabel = (method) => {
        const labels = {
            domicilio_ciudad: 'Domicilio Ciudad',
            domicilio_local: 'Domicilio Local',
            recogida_tienda: 'Recogida en Tienda',
            envio_nacional: 'Envío Nacional',
            envio_internacional: 'Envío Internacional',
            contraentrega: 'Contraentrega'
        };
        return labels[method] || method || 'No especificado';
    };

    const itemsCount = Array.isArray(order?.items) ? order.items.length : (order?.items_count || 0);

    return (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-2 md:p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-gray-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header - Fixed & Premium */}
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-3 flex items-center justify-between flex-shrink-0 text-white">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/20">
                            <Package className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black tracking-tight leading-tight">
                                #{order.orderNumber}
                            </h2>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                Gestión de Entrega • {itemsCount} ITEMS
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-full transition-all text-gray-400 hover:text-white"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content Area - Grid System to avoid vertical scroll */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50 p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full">

                        {/* LEFT COLUMN: Main Info & Payment (7 cols) */}
                        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {/* Address & Customer Card */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3">
                                    <MapPin className="w-4 h-4 text-blue-500 opacity-20 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Dirección de Entrega</h3>
                                <div className="space-y-3">
                                    <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 group-hover:border-blue-300 transition-colors">
                                        <p className="text-sm font-black text-gray-900 leading-tight">
                                            {order.deliveryAddress || order.delivery_address || 'Sin dirección'}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                                <User className="w-4 h-4 text-gray-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-black text-gray-900">{order.customerName}</p>
                                                <p className="text-[10px] text-gray-500 font-bold">{order.customerPhone || 'Sin teléfono'}</p>
                                            </div>
                                        </div>
                                        {order.customerPhone && (
                                            <div className="flex gap-2">
                                                <a
                                                    href={`tel:${order.customerPhone}`}
                                                    className="w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center hover:bg-green-600 hover:text-white transition-all shadow-sm"
                                                >
                                                    <Phone className="w-4 h-4" />
                                                </a>
                                                <button className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                                                    <Navigation className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Order Context Card (Shipping, Payment, Date) */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 grid grid-cols-2 gap-3">
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Envío</p>
                                    <div className="flex items-center gap-2">
                                        <Truck className="w-3 h-3 text-gray-500" />
                                        <span className="text-xs font-black text-gray-800 truncate">{getDeliveryMethodLabel(order.delivery_method)}</span>
                                    </div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Pago</p>
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="w-3 h-3 text-gray-500" />
                                        <span className="text-xs font-black text-gray-800 truncate">{getPaymentMethodLabel(order.paymentMethod || order.payment_method)}</span>
                                    </div>
                                </div>
                                <div className="col-span-2 p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-3 h-3 text-gray-500" />
                                        <div>
                                            <p className="text-[9px] font-black text-gray-400 uppercase">Fecha Pedido</p>
                                            <p className="text-xs font-black text-gray-800">
                                                {order.shipping_date ? format(new Date(order.shipping_date), "d MMM", { locale: es }) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="bg-blue-100 text-blue-700 text-[9px] font-black px-2 py-0.5 rounded-full uppercase">Hace 1 día</span>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Payment Matrix - ULTRA HIGH DENSITY */}
                            <div className={`rounded-2xl border-2 p-1 overflow-hidden transition-all ${totalDue > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                                <div className="bg-white rounded-[14px] p-4 shadow-sm h-full">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            {totalDue > 0 ? <AlertCircle className="w-4 h-4 text-red-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                                            <span className={`text-xs font-black uppercase tracking-widest ${totalDue > 0 ? 'text-red-700' : 'text-emerald-700'}`}>Información de Pago</span>
                                        </div>
                                        {totalDue > 0 && <span className="bg-red-600 text-white text-[9px] font-black px-2 py-0.5 rounded uppercase pulse">Cobrar</span>}
                                    </div>

                                    <div className="flex items-center justify-between gap-4">
                                        <div className="space-y-1">
                                            <p className="text-[10px] text-gray-400 font-bold uppercase">Valor Pedido</p>
                                            <p className="text-base font-bold text-gray-600">${fullTotal.toLocaleString('es-CO')}</p>
                                        </div>
                                        <div className="h-8 w-px bg-gray-100"></div>
                                        <div className="space-y-1 text-right">
                                            <p className="text-[10px] text-gray-400 font-bold uppercase">Ya Pagado</p>
                                            <p className="text-base font-bold text-emerald-600">${paid.toLocaleString('es-CO')}</p>
                                        </div>
                                    </div>

                                    <div className={`mt-4 p-4 rounded-xl flex items-center justify-between ${totalDue > 0 ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'bg-emerald-600 text-white shadow-lg shadow-emerald-200'}`}>
                                        <span className="text-xs font-black uppercase tracking-widest opacity-80">Saldo a Cobrar</span>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-2xl font-black">${totalDue.toLocaleString('es-CO')}</span>
                                            <span className="text-[10px] font-bold opacity-60">COP</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content Summary - Collapsible but high density */}
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
                                <button
                                    onClick={() => setShowItems(!showItems)}
                                    className="px-4 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <Package className="w-4 h-4 text-gray-400" />
                                        <span className="text-[11px] font-black text-gray-700 uppercase tracking-widest">Resumen de Contenido</span>
                                        <span className="bg-blue-100 text-blue-700 text-[10px] font-black px-2 py-0.5 rounded-full">{itemsCount}</span>
                                    </div>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showItems ? 'rotate-180' : ''}`} />
                                </button>
                                {showItems && (
                                    <div className="max-h-[160px] overflow-y-auto p-2 space-y-1 divide-y divide-gray-50">
                                        {Array.isArray(order.items) && order.items.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between py-1.5 px-2">
                                                <div className="min-w-0 flex-1 mr-4">
                                                    <p className="text-xs font-bold text-gray-800 truncate">{item.product_name || item.name}</p>
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">REF: {item.product_code || 'N/A'}</p>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-[11px] font-black text-gray-900">{item.quantity} x ${(item.unit_price ?? item.price ?? 0).toLocaleString('es-CO')}</p>
                                                    <p className="text-[10px] font-bold text-blue-500">${(item.quantity * (item.unit_price ?? item.price ?? 0)).toLocaleString('es-CO')}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Operational Notes - If any */}
                        {(order.delivery_notes || order.special_instructions) && (
                            <div className="lg:col-span-12 bg-amber-50 rounded-xl border border-amber-200 p-3 flex gap-3">
                                <div className="w-8 h-8 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
                                    <StickyNote className="w-4 h-4 text-amber-700" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-1">Notas de Mensajería</h4>
                                    <p className="text-xs text-amber-900 leading-snug italic font-medium">
                                        {order.delivery_notes || order.special_instructions}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Tracking Logs - High density toggle */}
                        <div className="lg:col-span-12 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                            <button
                                onClick={() => setShowTimeline(!showTimeline)}
                                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <History className="w-4 h-4 text-orange-500" />
                                    <span className="text-[11px] font-black text-gray-700 uppercase tracking-widest">Bitácora Técnica</span>
                                    <span className="bg-orange-100 text-orange-700 text-[10px] font-black px-2 py-0.5 rounded-full">{order.notes_count || '?'}</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTimeline ? 'rotate-180' : ''}`} />
                            </button>
                            {showTimeline && (
                                <div className="border-t border-gray-100 max-h-[300px] overflow-hidden">
                                    <OrderTimeline orderId={order.id} user={null} />
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Footer - Sticky */}
                <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-end gap-3 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-100 text-gray-700 font-black text-[11px] uppercase tracking-widest hover:bg-gray-200 rounded-xl transition-all border border-gray-300 shadow-sm"
                    >
                        Cerrar Ventana
                    </button>
                    <button
                        onClick={onClose}
                        className="px-8 py-2.5 bg-gray-900 text-white font-black text-[11px] uppercase tracking-widest hover:bg-black rounded-xl transition-all shadow-lg"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;
